/**
 * /my — "My registrations" for the signed-in individual: each Connect program
 * they've registered for, with a link back to /register?program=ID to edit.
 * Signed out → the same email sign-in as /register, landing back here.
 */
import { PageIntro } from "../components/PageIntro";
import { useBlock } from "../lib/content";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ClipboardList } from "lucide-react";
import { formatEventDates } from "@alight/contracts";
import { Badge, Banner, Button, Card, CardContent, Container, Section } from "@alight/ui";
import { useSeo } from "../lib/seo";
import { EmailSignIn, SignedInBar, useMe, useMyRegistrations } from "./register";

export const Route = createFileRoute("/my")({
  component: MyRegistrationsPage,
});

function formatDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}

function MyRegistrationsPage() {
  const banners = useBlock("banners");
  useSeo({ title: "My registrations | Connect Sydney", noindex: true });
  const me = useMe();
  const email = me.data?.email ?? null;
  const mine = useMyRegistrations(Boolean(email));

  let body: React.ReactNode;
  if (me.isLoading || (email && mine.isLoading)) {
    body = (
      <Card>
        <CardContent className="p-10 text-center text-sm text-muted" role="status">
          <span className="animate-pulse">Loading…</span>
        </CardContent>
      </Card>
    );
  } else if (!email) {
    body = (
      <Card>
        <CardContent className="p-6 sm:p-8">
          <h2 className="font-heading text-2xl font-extrabold text-ink">Sign in to see your registrations</h2>
          <p className="mt-2 text-muted">Use the email you registered with and we&apos;ll send you a sign-in link.</p>
          <div className="mt-6">
            <EmailSignIn redirect="/my" />
          </div>
        </CardContent>
      </Card>
    );
  } else if (mine.isError) {
    body = <Banner variant="error">We couldn&apos;t load your registrations just now. Please refresh to try again.</Banner>;
  } else {
    const registrations = mine.data?.registrations ?? [];
    const programs = new Map((mine.data?.programs ?? []).map((p) => [p.id, p]));
    body =
      registrations.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center sm:p-10">
            <ClipboardList className="mx-auto h-10 w-10 text-primary" aria-hidden />
            <h2 className="mt-4 font-heading text-2xl font-extrabold text-ink">No registrations yet</h2>
            <p className="mt-2 text-muted">When you register for Connect it will show up here.</p>
            <Button asChild className="mt-6">
              <Link to="/register">Register now</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <ul className="space-y-4">
          {registrations.map((r) => {
            const p = programs.get(r.program_id);
            const dates = p ? formatEventDates(p.start_date, p.end_date) : "";
            return (
              <li key={r.id}>
                <Card>
                  <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="font-heading text-xl font-bold text-ink">{p?.title ?? "Connect program"}</h2>
                        {r.status === "registered" ? (
                          <Badge variant="success">Registered</Badge>
                        ) : (
                          <Badge variant="muted">Withdrawn</Badge>
                        )}
                      </div>
                      {dates && <p className="mt-1 text-sm text-muted">{dates}</p>}
                      <p className="mt-1 text-sm text-muted">
                        Ref <span className="font-mono font-semibold text-ink">{r.ref}</span> · registered{" "}
                        {formatDay(r.created_at)}
                      </p>
                    </div>
                    {p?.open ? (
                      <Button asChild variant={r.status === "registered" ? "outline" : "default"} className="shrink-0">
                        <Link to="/register" search={{ program: String(r.program_id) }}>
                          {r.status === "registered" ? "View or edit" : "Register again"}
                        </Link>
                      </Button>
                    ) : (
                      <span className="text-sm text-muted">Registrations closed</span>
                    )}
                  </CardContent>
                </Card>
              </li>
            );
          })}
        </ul>
      );
  }

  return (
    <>
      <PageIntro
        image={banners.myImage}
        imageAlt={banners.myImageAlt}
        eyebrow="Your account"
        title="My registrations"
      />
      <Section tone="paper" spacing="lg">
        <Container width="narrow" className="space-y-6 px-0 sm:px-0 lg:px-0">
          {email && !me.isLoading && <SignedInBar email={email} />}
          {body}
        </Container>
      </Section>
    </>
  );
}
